import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import RecipeSummaryCardCompact from "../components/RecipeSummaryCardCompact";
import RecipeSummaryCardImage from "../components/RecipeSummaryCardImage";
import InspirationSlidePanel from "../components/InspirationSlidePanel";
import LoadingGallery from "../components/LoadingGallery";
import { useLoading } from "../contexts/LoadingContext";
import InspirationButton from "../components/InspirationButton";
import StickyChatButton from "../components/StickyChatButton";
import ChatCTA from "../components/ChatCTA";
import SubscribeBanner from "../components/SubscribeBanner";
import { ArrowRightIcon } from "@heroicons/react/24/solid";

export default function HomePage() {

  const { setSuppressLoader } = useLoading();
  const [todayRecipes, setTodayRecipes] = useState([]);
  const [todayLoading, setTodayLoading] = useState(true);


  const [seasonalRecipes, setSeasonalRecipes] = useState([]);
  const [recentRecipes, setRecentRecipes] = useState([]);
  const [favouriteRecipes, setFavouriteRecipes] = useState([]);
  const [topRecipes, setTopRecipes] = useState([]);

  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;
  const retryDelayMs = 5000;
  const [showInspiration, setShowInspiration] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    setSuppressLoader(true);
    return () => setSuppressLoader(false);
  }, [setSuppressLoader]);

  // ——— Fetch Today once ———
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const today = new Date().toISOString().split("T")[0];
        const res = await apiClient.get(`/calendar/day/${today}`);
        if (!cancelled) setTodayRecipes(res.data || []);
      } catch (err) {
        console.error("Today fetch error", err);
      } finally {
        if (!cancelled) setTodayLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);


  // ——— Fetch Categories with retry logic ———
  useEffect(() => {
    let cancelled = false;

    async function fetchCategories() {
      try {
        const [seasonalRes, recentRes, favRes, topRes] = await Promise.all([
          apiClient.get(`recipes/sous-recipes?filter=seasonal&limit=5`),
          apiClient.get(`users/recipes/recent?limit=5`),
          apiClient.get(`recipes/search?filter=favourites&limit=5`),
          apiClient.get(`recipes/search?filter=highRated&limit=5`),
        ]);
        if (cancelled) return;

        setSeasonalRecipes(seasonalRes.data.data || []);
        setRecentRecipes(recentRes.data || []);
        setFavouriteRecipes(favRes.data.data || []);
        setTopRecipes(topRes.data.data || []);

        const stillEmpty =
          (seasonalRes.data?.length || 0) === 0 &&
          (recentRes.data?.length || 0) === 0 &&
          (favRes.data.data?.length || 0) === 0 &&
          (topRes.data.data?.length || 0) === 0;

        if (stillEmpty && retryCount < maxRetries) {
          setTimeout(() => setRetryCount((c) => c + 1), retryDelayMs);
        }
      } catch (err) {
        console.error("Categories fetch error", err);
      }
    }

    fetchCategories();
    return () => { cancelled = true; };
  }, [retryCount]);

  // ——— Loading flags ———
  const categoriesLoading =
    seasonalRecipes.length === 0 &&
    recentRecipes.length === 0 &&
    favouriteRecipes.length === 0 &&
    topRecipes.length === 0 &&
    retryCount < maxRetries;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 mb-8 space-y-2">

      {/* Subscribe banner */}
      <section>
        <SubscribeBanner />
      </section>

      {/* Today */}
      {todayLoading ? (
        <LoadingGallery />
      ) : todayRecipes.length > 0 ? (
        <section className="pt-1 pb-4">
          <h2 className="text-lg font-semibold mb-2">Today</h2>
          <div className="flex space-x-4 overflow-x-auto scrollbar-hide pb-2">
            {todayRecipes.map((e) => (
              <div key={e._id} className="shrink-0 w-5/6 md:w-3/5">
                <RecipeSummaryCardCompact
                  recipe={e.recipeId}
                  index={e.recipeId._id}
                  showSummary
                />
              </div>
            ))}
          </div>
        </section>
      ) : (
            <section className="pt-1 pb-4">
              <h2 className="text-lg font-semibold mb-2">Today</h2>
              <div className="text-sm text-gray-600 italic mb-2">
                Nothing planned for today.
          </div>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate("/calendar")}
                  className="w-full bg-white border border-sage text-sage text-sm py-2 px-4 rounded-3xl hover:bg-sage/20"
                >
                  Add a recipe
                </button>
      {/*
                <button
                      onClick={() => navigate("/chat")}
                      className="w-1/2 bg-sage border border-sage text-white text-sm py-2 px-4 rounded-3xl hover:bg-sagedark"
                    >
                      Chat with Sous
                </button>
      */}
              </div>
            </section>
          )}

      <section>
        <div className="rounded-2xl border border-gray-300 bg-gradient-to-tr from-blue-200 to-champagnePink via-purple-300 p-4 pb-8 space-y-4">
          <div className="text-white">
            <h3 className="text-md font-semibold mb-2">Unsure what to cook?</h3>
            <p>Let's find a few ideas to get you feeling inspired for something wonderful.</p>
          </div>
          <InspirationButton onClick={() => setShowInspiration(true)} />

          <InspirationSlidePanel
            isOpen={showInspiration}
            onClose={() => setShowInspiration(false)}
          />
        </div>
      </section>

      {/* Categories */}
      {categoriesLoading ? (
        <LoadingGallery />
      ) : (
          <>
            {/* Recent */}
            <section>
              <h2 className="text-lg font-semibold text-charcoal mb-2">
                Recent recipes
            </h2>
              {recentRecipes.length > 0 ? (
                <div className="flex space-x-4 overflow-x-auto scrollbar-hide pb-2">
                  {recentRecipes.map((recipe) => (
                    <div key={recipe._id} className="shrink-0 w-5/6 md:w-3/5">
                      <RecipeSummaryCardImage key={recipe._id} recipe={recipe} index={recipe._id} />
                    </div>
                  ))}
                </div>
              ) : (
                  <div className="text-sm text-gray-600 italic">
                    No recent recipes
                  </div>
                )}
            </section>

            {/* Chat CTA */}
            <section className="py-4">
              <ChatCTA />
            </section>

            {/* Favourites */}
            <section>
              <h2 className="text-lg font-semibold text-charcoal mb-2">
                Your Favourites
            </h2>
              {favouriteRecipes.length > 0 ? (
                <div className="flex space-x-4 overflow-x-auto scrollbar-hide pb-2">
                  {favouriteRecipes.map((recipe) => (
                    <div key={recipe._id} className="shrink-0 w-5/6 md:w-3/5">
                      <RecipeSummaryCardImage key={recipe._id} recipe={recipe} index={recipe._id} />
                    </div>
                  ))}
                </div>
              ) : (
                  <div className="text-sm text-gray-600 italic">
                    You haven’t favourited any recipes yet
                  </div>
                )}
            </section>

            {/* Top Rated */}
            <section>
              <h2 className="text-lg font-semibold text-charcoal mb-2">
                Your top rated
            </h2>
              {topRecipes.length > 0 ? (
                <div className="flex space-x-4 overflow-x-auto scrollbar-hide pb-2">
                  {topRecipes.map((recipe) => (
                    <div key={recipe._id} className="shrink-0 w-5/6 md:w-3/5">
                      <RecipeSummaryCardImage key={recipe._id} recipe={recipe} index={recipe._id} />
                    </div>
                  ))}
                </div>
              ) : (
                  <div className="text-sm text-gray-600 italic">
                    You haven’t rated any recipes very highly yet
                  </div>
                )}
            </section>

            {/* SEASONAL */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold text-charcoal">
                  Seasonal recipes
                </h2>
                <button
                  type="button"
                  className="flex items-center text-sm font-medium text-charcoal hover:text-sage"
                  onClick={() => navigate("/sous-recipes")}
                >
                  View more
                  <ArrowRightIcon className="w-4 h-4 ml-1" />
                </button>
              </div>

              {seasonalRecipes?.length > 0 ? (
                <div className="flex space-x-4 overflow-x-auto scrollbar-hide pb-2">
                  {seasonalRecipes.map((recipe) => (
                    <div key={recipe._id} className="shrink-0 w-5/6 md:w-3/5">
                      <RecipeSummaryCardImage recipe={recipe} index={recipe._id} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-600 italic">
                  It's a little empty here...
                </div>
              )}
            </section>

          </>
          
        )}
      <StickyChatButton />

    </div>
  );
}
